import type { ReactNode } from 'react'

import { PageHero } from '@/components/generals/page-hero'
import { Reveal } from '@/components/generals/reveal'

export interface LegalSection {
    id: string
    title: string
    body: Array<ReactNode>
}

interface LegalPageProps {
    eyebrow: string
    title: string
    intro: string
    updated: string
    sections: Array<LegalSection>
}

/** Calm, readable layout shared by the privacy policy and the terms. */
export const LegalPage = ({ eyebrow, title, intro, updated, sections }: LegalPageProps) => {
    return (
        <>
            <PageHero eyebrow={eyebrow} title={title} subtitle={intro} />

            <section className='pb-28'>
                <div className='shell grid gap-12 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-20'>
                    <nav aria-label='On this page' className='hidden lg:block'>
                        <div className='sticky top-28'>
                            <p className='text-[11px] font-medium uppercase tracking-[0.28em] text-cream/40'>
                                On this page
                            </p>

                            <ul className='mt-5 space-y-2.5 text-sm'>
                                {sections.map(section => (
                                    <li key={section.id}>
                                        <a href={`#${section.id}`} className='text-cream/60 transition-colors hover:text-cream'>
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </nav>

                    <article className='max-w-3xl'>
                        <p className='text-sm text-cream/45'>
                            Last updated {updated}
                        </p>

                        {sections.map(section => (
                            <Reveal as='section' key={section.id} id={section.id} y={16} className='scroll-mt-28 border-t border-white/[0.08] py-10 first-of-type:mt-6'>
                                <h2 className='font-serif text-3xl italic text-cream'>
                                    {section.title}
                                </h2>

                                <div className='mt-4 space-y-4 text-[15px] leading-relaxed text-cream/70'>
                                    {section.body.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                                </div>
                            </Reveal>
                        ))}
                    </article>
                </div>
            </section>
        </>
    )
}
